import { Authorization, NetworkError } from "../state/authAtoms";
import {
	IsMaskVisibleSelector,
	SelectedTourVariantAtom,
	ShowFileUploader,
	ShowTour,
	ShowTourSelectionAtom,
} from "../state/uiAtoms";
import { TourProvider } from "@reactour/tour";
import { getTourSteps, qpTourStyles } from "../lib/tour/tour";
import { TourAnalyticsBridge } from "../lib/tour/TourAnalyticsBridge";
import { lightTheme, measures, strings, text } from "@quickpoint.me/shared";
import styled, { ThemeProvider } from "styled-components";
import {
	useRecoilValue,
	useSetRecoilState,
} from "recoil";

import Auth from "./auth/Auth";
import Editor from "./editor/Editor";
import FileDrop from "../functionalComponents/FileDrop";
import { LocaleAtom } from "../state/localeAtom";
import FileUploader from "@quickpoint.me/fileuploader";
import FrameSize from "../functionalComponents/FrameSize";
import { HostId } from "../state/atoms";
import LogSuspense from "./LogSuspense";
import { PreviewBar } from "./previewbar/PreviewBar";
import PreviewPane from "./preview/PreviewPane";
import { QpTourContext } from "../lib/tour/wrapper";
import RefreshSpinner from "./Refresh";
import Share from "./Share";
import TknsPanel from "./TknsPanel";
import Stacks from "./stacks/Stacks";
import { Suspense } from "react";
import TopBar from "./TopBar";
import TourSelectionModal from "./TourSelectionModal";
import VocabBar from "./vocabbar/VocabBar";

const StyledApp = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: ${(props) => props.theme.backgroundColor};
  width: 100svw;
  height: ${measures.styledApp.height};
`;

const StyledLandscapeGrid = styled.div`
  display: grid;
  grid-template-rows: ${measures.topBar.landscape.height} ${measures.previewAndEditor.landscape.editorRowHeight} ${measures.vocabBar.landscape.rowHeight} ${measures.previewBar.height};
  grid-template-columns: ${measures.gridColumns.landscape.left} ${measures.gridColumns.landscape.right};
  grid-template-areas:
    "top-bar top-bar"
    "editor previews"
    "vocabbar previews"
    "thumbnails thumbnails";
  width: 100svw;
  height: 100svh;
`;

const StyledVocabBarArea = styled.div`
  grid-area: vocabbar;
  background-color: ${(props) => props.theme.backgroundColor};
  min-width: 0;
  overflow: hidden;
  padding-top: 0.5svh;
  padding-bottom: 0.5svh;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: ${measures.editor.landscape.gutter};
    right: 0;
    height: 1px;
    top: 0;
    background-color: ${(props) => props.theme.navigationFrame};
  }
`;

const SkipToContent = styled.a`
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 100;
  padding: 15px 20px; /* 48px gold-standard target */
  min-height: 48px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  /* 7.8:1 with white text (AAA) — the old #4A90D9 was 3.34:1, an AA fail */
  background: #004FAA;
  color: #fff;
  font-family: sans-serif;
  font-size: 0.9rem;
  text-decoration: none;
  border-radius: 0 0 4px 4px;

  &:focus {
    left: 50%;
    transform: translateX(-50%);
  }
`;

const StyledMask = styled.div<{ $dim: boolean }>`
  position: absolute;
  display: ${(props) => (props.$dim ? "block" : "none")};
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: rgb(0 0 0 / 34%);
  z-index: 2;


`;

/** Inner content — child of TourProvider. */
function LandscapeContent({ hostId }: { hostId: string }) {
	const networkError = useRecoilValue(NetworkError);
	const showFileUploader = useRecoilValue(ShowFileUploader);
	const isInPortrait = false;
	const isMaskVisible = useRecoilValue(IsMaskVisibleSelector);

	if (networkError) {
		return (
			<StyledApp>
				<p>{text.ui.networkError}</p>
			</StyledApp>
		);
	}

	return (
		<>
			<Suspense
				fallback={
					<RefreshSpinner fullScreen showBackGround={true} area="Auth" />
				}
			>
				<Auth />
			</Suspense>

			<StyledApp
				data-testid="userInterface"
				className={`${strings.css.authorUserInterface} ${strings.css.fileDropZone}`}
			>
				<Suspense fallback={null}>
					<TourSelectionModal />
				</Suspense>
				<Share />
				<TknsPanel />
				<Suspense fallback={<LogSuspense />}>
					<Stacks />
				</Suspense>
				<FrameSize />
				<Suspense fallback={<LogSuspense />}>
					<FileDrop />
				</Suspense>
				<FileUploader
					show={showFileUploader}
					isInPortrait={isInPortrait}
					hostId={hostId}
				/>
				<StyledMask $dim={isMaskVisible} />
				<StyledLandscapeGrid>
					<SkipToContent href="#main-editor">{text.ui.skipToEditor}</SkipToContent>
					<TopBar />
					<Suspense fallback={<RefreshSpinner showBackGround={true} />}>
						<Editor />
					</Suspense>

					<StyledVocabBarArea id="vocabbar">
						<VocabBar />
					</StyledVocabBarArea>
					<PreviewBar
						$hostId={hostId}
						landscape={true}
						cssId={strings.css.landscapePreviewGeneratorID}
					/>
					<Suspense fallback={<LogSuspense />}>
						<PreviewPane />
					</Suspense>
				</StyledLandscapeGrid>
			</StyledApp>
		</>
	);
}

function UserInterfaceLandscape() {
	const hostId = useRecoilValue(HostId);
	const auth = useRecoilValue(Authorization);
	const locale = useRecoilValue(LocaleAtom);
	const selectedTourVariant = useRecoilValue(SelectedTourVariantAtom);
	const setShowTour = useSetRecoilState(ShowTour);
	const setShowTourSelection = useSetRecoilState(ShowTourSelectionAtom);

	const closeTour: React.ComponentProps<typeof TourProvider>["onClickClose"] = ({ setCurrentStep }) => {
		setCurrentStep(0);
		setShowTour(false);
		setShowTourSelection(true);
	};

	return (
		<ThemeProvider theme={lightTheme}>
			<QpTourContext.Provider value={{ isAuthenticated: auth.isLoggedIn }}>
				<TourProvider
					key={`${selectedTourVariant}-${locale}`}
					onClickHighlighted={(e) => {
						e.stopPropagation();
					}}
					disableInteraction
					showDots={false}
					disableKeyboardNavigation={false}
					steps={getTourSteps(hostId, selectedTourVariant)}
					className="qp-tour"
					styles={qpTourStyles}
					onClickClose={closeTour}
					onClickMask={() => null}
				>
					<TourAnalyticsBridge variant={selectedTourVariant} />
					<LandscapeContent key={locale} hostId={hostId} />
				</TourProvider>
			</QpTourContext.Provider>
		</ThemeProvider>
	);
}

export default UserInterfaceLandscape;
